import { makeStyles } from "@fluentui/react-components";
import { Client } from "@microsoft/microsoft-graph-client";
import {
  CellChange,
  CellLocation,
  CheckboxCell,
  Column,
  DefaultCellTypes,
  Id,
  Range,
  ReactGrid,
  Row,
  TextCell,
} from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Field } from "../../models/Field";
import { FieldValues } from "../../models/FieldValues";
import { UserInformation } from "../../models/UserInformation";
import { VsaaTag } from "../../models/VsaaTag";
import { EntityType } from "../../services/MassPublishService";
import {
  getBooleanFieldValue,
  getTagFieldValue,
} from "../../services/FieldService";
import { VsaaColorTokens } from "../../style/VsaaColorTokens";
import { TagCell, TagCellTemplate, TagCellValue } from "./TagCellTemplate";
import { UserCell, UserCellTemplate, UserCellValue } from "./UserCellTemplate";

export interface IQuickEditableEntity {
  versionId: string;
  title: string;
  fileName?: string;
  webUrl?: string;
  fieldValues?: FieldValues;
}

const useStyles = makeStyles({
  gridContainer: {
    overflow: "auto",
    // Overrides ReactGrid styling for the position of check marks in checkboxes.
    // Our styling scewed this somewhat in ReactGrid so corrected ReactGrids styling here.
    "& .reactgrid-content .rg-pane .rg-cell.rg-checkbox-cell label span:after":
      {
        left: "6px",
        top: "1px",
      },
    "& .reactgrid-content .rg-pane .rg-cell.rg-text-cell.rg-cell-nonEditable.readOnlyFileNameCell":
      {
        backgroundColor: VsaaColorTokens.color_file_extension_badge_background,
        color: VsaaColorTokens.color_file_extension_badge_text,
      },
  },
  // This div handles vertical scrollbar that would appear on any size of the grid when a cell on the last row is selected.
  gridClearHeight: {
    height: "20px",
  },
});

interface GenericReactGridProps<T extends IQuickEditableEntity> {
  entities: T[];
  selectedEntityIds?: string[];
  entityFields?: Field[];
  fieldOrder?: string[];
  defaultFocusLocation?: CellLocation;
  onFocusLocationChange?: (newLocation: CellLocation | undefined) => void;
  onGridChanges: (updatedEntities: T[]) => void;
  usedTagsId?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  toolType?: string;
  showFileNameColumn?: boolean;
  entityType?: EntityType;
  graphClient?: Client | null;
  isFieldReadOnly?: (entity: T, field: Field) => boolean;
  isFieldChangeBlocked?: (entity: T, field: Field, value: unknown) => boolean;
  onFieldChangeBlocked?: (entity: T, field: Field) => void;
}

enum BuiltInGridColumnIds {
  selection = "selection",
  title = "title",
  fileName = "fileName",
  webUrl = "webUrl",
}

export const getFieldsInOrder = (
  entityFields?: Field[],
  fieldOrder?: string[]
) => {
  if (!entityFields || !fieldOrder) {
    return entityFields;
  }

  const fieldsByUniqueName = new Map(
    entityFields.map((field) => [field.uniqueName, field])
  );
  const orderedFields = fieldOrder.flatMap((fieldName) => {
    const field = fieldsByUniqueName.get(fieldName);
    return field ? [field] : [];
  });
  const unorderedFields = entityFields.filter(
    (field) => !fieldOrder.includes(field.uniqueName)
  );

  return [...orderedFields, ...unorderedFields];
};

const GenericReactGrid = <T extends IQuickEditableEntity>(
  props: GenericReactGridProps<T>
) => {
  const {
    entities,
    selectedEntityIds,
    entityFields,
    fieldOrder,
    defaultFocusLocation,
    onFocusLocationChange,
    onGridChanges,
    usedTagsId,
    onSelectionChange,
    toolType,
    showFileNameColumn,
    entityType,
    graphClient,
    isFieldReadOnly,
    isFieldChangeBlocked,
    onFieldChangeBlocked,
  } = props;
  const classes = useStyles();
  const orderedEntityFields = useMemo(
    () => getFieldsInOrder(entityFields, fieldOrder),
    [entityFields, fieldOrder]
  );
  const [columns, setColumns] = useState<Column[]>(() => {
    const newColumns: Column[] = [];

    if (selectedEntityIds) {
      newColumns.push({
        columnId: BuiltInGridColumnIds.selection,
        width: 45,
        resizable: false,
      });
    }

    // Add webUrl column for links library only
    if (toolType === "Link") {
      newColumns.push({
        columnId: BuiltInGridColumnIds.webUrl,
        width: 200,
        resizable: true,
      });
    }

    if (showFileNameColumn) {
      newColumns.push({
        columnId: BuiltInGridColumnIds.fileName,
        width: 200,
        resizable: true,
      });
    }

    newColumns.push({
      columnId: BuiltInGridColumnIds.title,
      width: 200,
      resizable: true,
    });

    if (orderedEntityFields) {
      orderedEntityFields.forEach((field) => {
        const shouldIncludeField = field.editable;

        if (!shouldIncludeField) {
          return;
        }

        // Exclude PeoplePicker (type 3) fields from quick editor if no graphClient
        if (field.type === 3 && !graphClient) {
          return;
        }

        let col: Column;

        if (field.type === 1 || field.type === 2) {
          col = {
            columnId: field.uniqueName,
            width: 80,
            resizable: false,
          };
        } else if (field.type === 3) {
          col = {
            columnId: field.uniqueName,
            width: 200,
            resizable: true,
          };
        } else {
          col = {
            columnId: field.uniqueName,
            width: 150,
            resizable: true,
          };
        }

        newColumns.push(col);
      });
    }

    return newColumns;
  });

  const { t } = useTranslation();
  const [updatedEntities, setUpdatedEntities] = useState<T[]>([]);

  /* Combines the updated entities with non updated entities for the full representation of the Grid */
  const currentEntities = useMemo<T[]>(() => {
    if (updatedEntities.length === 0) {
      return entities;
    }

    const newCurrentEntities: T[] = [];

    entities.forEach((entity) => {
      const updatedEntity = updatedEntities.find(
        (a) => a.versionId === entity.versionId
      );

      if (updatedEntity) {
        newCurrentEntities.push(updatedEntity);
      } else {
        newCurrentEntities.push(entity);
      }
    });

    return newCurrentEntities;
  }, [entities, updatedEntities]);

  const getHeaderRow = (): Row<DefaultCellTypes | TagCell | UserCell> => {
    const headerRow: Row<DefaultCellTypes | TagCell | UserCell> = {
      rowId: "header",
      cells: [],
    };

    if (selectedEntityIds) {
      headerRow.cells.push({ type: "header", text: "" });
    }

    // Add webUrl header for links library only
    if (toolType === "Link") {
      headerRow.cells.push({ type: "header", text: t("webUrl") });
    }

    if (showFileNameColumn) {
      headerRow.cells.push({
        type: "header",
        text: t(toolType === "Document" ? "name" : "fileName"),
      });
    }

    headerRow.cells.push({
      type: "header",
      text: t(
        entityType === "activity"
          ? "dataGridFieldLabels.activityTitle"
          : "title"
      ),
    });

    if (!orderedEntityFields) {
      return headerRow;
    }

    orderedEntityFields.forEach((field) => {
      const shouldIncludeField = field.editable;

      if (!shouldIncludeField) {
        return;
      }

      if (field.type === 3 && !graphClient) {
        return;
      }

      const cell: DefaultCellTypes | TagCell | UserCell = {
        type: "header",
        text: field.displayName,
      };

      headerRow.cells.push(cell);
    });

    return headerRow;
  };

  const handleColumnResize = (ci: Id, width: number) => {
    setColumns((prevColumns) => {
      const columnIndex = prevColumns.findIndex((el) => el.columnId === ci);
      const resizedColumn = prevColumns[columnIndex];
      const updatedColumn = { ...resizedColumn, width };
      prevColumns[columnIndex] = updatedColumn;
      return [...prevColumns];
    });
  };

  const getTagDefaultValue = (
    fieldName: string,
    tagCollectionId: string,
    label: string,
    multiValue: boolean,
    tags?: VsaaTag[]
  ) => {
    const tagValue: TagCellValue = {
      fieldName,
      label: label,
      tagCollectionId: tagCollectionId,
      multiValue,
      tags: tags ? tags : [],
    };

    return tagValue;
  };

  const getRow = (
    entity: T,
    idx: number
  ): Row<DefaultCellTypes | TagCell | UserCell> => {
    const row: Row<DefaultCellTypes | TagCell | UserCell> = {
      rowId: idx,
      height: 35,
      cells: [],
    };

    if (selectedEntityIds) {
      row.cells.push({
        type: "checkbox",
        checked: selectedEntityIds.includes(entity.versionId),
      });
    }

    // Add webUrl cell for links library only
    if (toolType === "Link") {
      const webUrlCell: TextCell = {
        type: "text",
        text: entity.webUrl || "",
        nonEditable: false,
      };
      row.cells.push(webUrlCell);
    }

    if (showFileNameColumn) {
      const fileNameCell: TextCell = {
        className: "readOnlyFileNameCell",
        type: "text",
        text: entity.fileName || "",
        nonEditable: true,
      };
      row.cells.push(fileNameCell);
    }

    const titleCell: TextCell = { type: "text", text: entity.title };
    row.cells.push(titleCell);

    if (!orderedEntityFields) {
      return row;
    }

    orderedEntityFields.forEach((field) => {
      const shouldIncludeField = field.editable;

      if (!shouldIncludeField) {
        return;
      }

      if (field.type === 3 && !graphClient) {
        return;
      }

      if (field.type === 0) {
        const tagValue = getTagFieldValue(field.uniqueName, entity.fieldValues);

        if (!field.tagCollectionId) {
          throw Error(
            `Tag field ${field.uniqueName} is missing tag collection id`
          );
        }

        const tagDefaultValue: TagCellValue = getTagDefaultValue(
          field.uniqueName,
          field.tagCollectionId,
          field.displayName,
          field.multiValue,
          tagValue
        );

        const cell: TagCell = {
          type: "tags",
          text: JSON.stringify(tagDefaultValue),
          tagValue: tagDefaultValue,
        };

        row.cells.push(cell);
      } else if (field.type === 1 || field.type === 2) {
        let booleanValue = getBooleanFieldValue(
          field.uniqueName,
          entity.fieldValues
        );

        if (booleanValue === undefined && field.defaultValue !== undefined) {
          booleanValue = field.defaultValue as boolean;
        }

        const isArchived = getBooleanFieldValue("archived", entity.fieldValues);
        const fieldReadOnly = isFieldReadOnly?.(entity, field) === true;

        const cell: CheckboxCell = {
          type: "checkbox",
          checked: booleanValue,
          nonEditable:
            fieldReadOnly ||
            (field.uniqueName === "active" && isArchived === true)
              ? true
              : undefined,
        };

        row.cells.push(cell);
      } else if (field.type === 3) {
        const rawUsers = entity.fieldValues?.users[field.uniqueName] || [];
        const users: UserInformation[] = (rawUsers as UserInformation[]) || [];

        const userCellValue: UserCellValue = {
          label: field.displayName,
          users: users,
        };

        const cell: UserCell = {
          type: "users",
          text: JSON.stringify(userCellValue),
          userValue: userCellValue,
        };

        row.cells.push(cell);
      } else if (field.type === 4) {
        const urlValue = entity.fieldValues?.urls[field.uniqueName];
        const displayText = urlValue?.displayName || urlValue?.url || "";

        const cell: TextCell = {
          type: "text",
          text: displayText,
          nonEditable: true,
        };

        row.cells.push(cell);
      }
    });

    return row;
  };

  const getRows = (): Row<DefaultCellTypes | TagCell | UserCell>[] => [
    getHeaderRow(),
    ...currentEntities.map<Row<DefaultCellTypes | TagCell | UserCell>>(getRow),
  ];

  const isValidTagCellChange = (
    change: CellChange<DefaultCellTypes | TagCell | UserCell>
  ) => {
    const oldCell = change.previousCell as TagCell;
    const updatedCell = change.newCell as TagCell;

    if (oldCell.tagValue?.label !== updatedCell.tagValue?.label) {
      return false;
    }

    return true;
  };

  const onCellsChange = (changes: CellChange[]) => {
    const updates: T[] = [...updatedEntities];
    const nextSelectedIds = new Set(selectedEntityIds);

    changes.forEach((change) => {
      if (change.columnId === BuiltInGridColumnIds.selection) {
        const entity = currentEntities[change.rowId as number];
        if (!entity) return;

        if ((change.newCell as CheckboxCell).checked) {
          nextSelectedIds.add(entity.versionId);
        } else {
          nextSelectedIds.delete(entity.versionId);
        }
        return;
      }

      const updatedEntity = getUpdatedEntity(change, updates);

      if (updatedEntity) {
        updates.push(updatedEntity);
      }
    });

    if (
      changes.some(
        (change) => change.columnId === BuiltInGridColumnIds.selection
      )
    ) {
      onSelectionChange?.(Array.from(nextSelectedIds));
    }

    if (
      changes.some(
        (change) => change.columnId !== BuiltInGridColumnIds.selection
      )
    ) {
      setUpdatedEntities(updates);
      onGridChanges(updates);
    }
  };

  const getDeepClone = (entity: T) => {
    return JSON.parse(JSON.stringify(entity));
  };

  const getUpdatedEntity = (
    change: CellChange<DefaultCellTypes | TagCell | UserCell>,
    updates: T[]
  ): T | undefined => {
    const targetEntity = entities[change.rowId as number];
    const targetUpdatedEntity = updates.find(
      (a) => a.versionId === targetEntity.versionId
    );

    let update: T;

    if (targetUpdatedEntity) {
      // Entity has previous update. Use that reference.
      update = targetUpdatedEntity;
    } else {
      // Entity has no previous update. Create clone to use as the updated state.
      update = getDeepClone(targetEntity);
    }

    if (change.columnId === BuiltInGridColumnIds.title) {
      const updatedCell = change.newCell as TextCell;
      update.title = updatedCell.text;
    } else if (change.columnId === BuiltInGridColumnIds.webUrl) {
      const updatedCell = change.newCell as TextCell;
      update.webUrl = updatedCell.text;
    } else {
      const fieldUniqueName = change.columnId as string;

      // Get the field representing the column in the grid.
      const field: Field | undefined = entityFields?.find(
        (f) => f.uniqueName === fieldUniqueName
      );

      if (!field) {
        throw Error(
          `Field ${fieldUniqueName} was not found in the entities fields.`
        );
      }

      // If the field collection has not yet been created for the entity we create it here.
      if (!update.fieldValues) {
        update.fieldValues = { booleans: {}, tags: {}, users: {}, urls: {} };
      }

      if (field.type === 0) {
        // Tags
        if (!isValidTagCellChange(change)) {
          return;
        }

        const updatedCell = change.newCell as TagCell;

        update.fieldValues.tags[field.uniqueName] = updatedCell.tagValue.tags;
      } else if (field.type === 1 || field.type === 2) {
        const updatedCell = change.newCell as CheckboxCell;

        if (isFieldChangeBlocked?.(targetEntity, field, updatedCell.checked)) {
          onFieldChangeBlocked?.(targetEntity, field);
          return;
        }

        update.fieldValues.booleans[field.uniqueName] = updatedCell.checked;

        // When archived is set to true, automatically deactivate
        if (field.uniqueName === "archived" && updatedCell.checked === true) {
          update.fieldValues.booleans["active"] = false;
        }
      } else if (field.type === 3) {
        const updatedCell = change.newCell as UserCell;
        update.fieldValues.users[field.uniqueName] =
          updatedCell.userValue.users;
      }
    }

    if (targetUpdatedEntity) {
      // We use an existing reference so no need to return it here.
      return;
    }

    return update;
  };

  const rows = getRows();
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    setUpdatedEntities([]);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [entities]);

  const handleSelectionChange = (selectedRanges: Range[]) => {
    if (onSelectionChange) {
      const selectedRowIds = new Set<number>();
      selectedRanges.forEach((range) => {
        const firstRowId = range.first.row;
        const lastRowId = range.last.row;

        let startRow: number;
        let endRow: number;

        if (typeof firstRowId === "number") {
          startRow = firstRowId;
          endRow = typeof lastRowId === "number" ? lastRowId : firstRowId;
        } else {
          startRow =
            (firstRowId as unknown as { idx?: number; rowId?: number }).idx ||
            (firstRowId as unknown as { idx?: number; rowId?: number }).rowId ||
            0;
          endRow =
            (lastRowId as unknown as { idx?: number; rowId?: number }).idx ||
            (lastRowId as unknown as { idx?: number; rowId?: number }).rowId ||
            startRow;
        }

        for (let rowId = startRow; rowId <= endRow; rowId++) {
          const entityIndex = rowId - 1;
          if (entityIndex >= 0 && entityIndex < currentEntities.length) {
            selectedRowIds.add(entityIndex);
          }
        }
      });

      const selectedEntityIds = Array.from(selectedRowIds)
        .map((index) => currentEntities[index]?.versionId)
        .filter((id) => id !== undefined) as string[];

      onSelectionChange(selectedEntityIds);
    }
  };

  return (
    <div className={classes.gridContainer}>
      <ReactGrid
        enableRowSelection
        enableFillHandle
        initialFocusLocation={defaultFocusLocation}
        onFocusLocationChanged={onFocusLocationChange}
        stickyLeftColumns={columns.length > 1 ? 1 : 0}
        rows={rows}
        columns={columns}
        onCellsChanged={onCellsChange}
        onColumnResized={handleColumnResize}
        enableRangeSelection
        customCellTemplates={{
          tags: TagCellTemplate(usedTagsId),
          users: UserCellTemplate(
            graphClient || null,
            import.meta.env.VITE_USER_SEARCH_GROUP_ID
          ),
        }}
        stickyTopRows={1}
        onSelectionChanged={
          selectedEntityIds ? undefined : handleSelectionChange
        }
      />
      <div className={classes.gridClearHeight}></div>
    </div>
  );
};

export default GenericReactGrid;
